import React, { useState } from 'react';
import { Helmet } from "react-helmet-async";
import { AppBar, Box, Button, Card, CardContent, Container, Toolbar, Typography } from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Logo } from "../logo";
import { useSettings } from "../../contexts/Settings/settings-context";
import { ProductFeatures } from "../auth/product-features";
import TextInput from "../Form/TextInput";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import toast from "react-hot-toast";
import { Auth } from "../../api/Endpoints/Auth";

const emailSchema = yup.object().shape({
    email: yup.string().email('Enter a valid email').required('Email is required'),
});

const resetSchema = yup.object().shape({
    email: yup.string().email().required(),
    token: yup.string().required('Reset token is required'),
    password: yup.string().min(8, 'Password must be at least 8 characters').required('New password is required'),
    confirm_password: yup.string()
        .oneOf([yup.ref('password')], 'Passwords must match')
        .required('Confirm your password'),
});

const ForgotPassword = () => {
    const navigate = useNavigate();
    const settings = useSettings();
    const [step, setStep] = useState(1); // 1 = email, 2 = token + new password
    const [emailSent, setEmailSent] = useState('');
    const [sendLoading, setSendLoading] = useState(false);
    const [resetLoading, setResetLoading] = useState(false);

    const emailForm = useForm({
        mode: 'onBlur',
        resolver: yupResolver(emailSchema),
    });

    const resetForm = useForm({
        mode: 'onBlur',
        resolver: yupResolver(resetSchema),
    });

    const handleSendResetLink = async (data) => {
        setSendLoading(true);
        try {
            await Auth.forgotPassword({ email: data.email });
            toast.success('If this email is registered, a reset link has been sent. Check your inbox.');
            setEmailSent(data.email);
            resetForm.setValue('email', data.email);
            setStep(2);
        } catch (error) {
            const message = error?.response?.data?.message || error?.message || 'Something went wrong';
            toast.error(message);
        } finally {
            setSendLoading(false);
        }
    };

    const handleResetPassword = async (data) => {
        setResetLoading(true);
        try {
            await Auth.resetPassword({
                email: data.email,
                token: data.token,
                password: data.password,
            });
            toast.success('Password reset successfully. Please sign in.');
            navigate('/');
        } catch (error) {
            const message = error?.response?.data?.message || error?.message || 'Reset failed';
            toast.error(message);
        } finally {
            setResetLoading(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>Forgot password | Soul of Work</title>
            </Helmet>
            <AppBar
                elevation={0}
                sx={{ backgroundColor: 'background.paper' }}
            >
                <Container maxWidth="md">
                    <Toolbar
                        disableGutters
                        sx={{ height: 64 }}
                    >
                        <RouterLink to="/">
                            <Logo variant={settings.theme === 'dark' ? 'light' : 'dark'} />
                        </RouterLink>
                    </Toolbar>
                </Container>
            </AppBar>

            <Box
                sx={{
                    backgroundColor: 'background.default',
                    minHeight: '100%',
                    pt: '64px'
                }}
            >
                <Box sx={{ py: 9 }}>
                    <Container maxWidth="md">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                            <div className="hidden md:block">
                                <ProductFeatures />
                            </div>
                            <div className="w-full">
                                {step === 1 && (
                                    <form onSubmit={emailForm.handleSubmit(handleSendResetLink)}>
                                        <Card
                                            sx={{ backgroundColor: 'background.default' }}
                                            elevation={0}
                                        >
                                            <CardContent>
                                                <Box sx={{ alignItems: 'center', display: 'flex', mb: 3 }}>
                                                    <Typography color="textPrimary" variant="h4">
                                                        Forgot Password
                                                    </Typography>
                                                    <Box sx={{ flexGrow: 1 }} />
                                                </Box>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                                    Enter your email address and we'll send you a reset token.
                                                </Typography>
                                                <div className="flex flex-col gap-4">
                                                    <div className="w-full">
                                                        <TextInput
                                                            control={emailForm.control}
                                                            name="email"
                                                            label="Email address"
                                                            value={emailForm.watch('email')}
                                                        />
                                                    </div>
                                                    <LoadingButton
                                                        fullWidth
                                                        color="primary"
                                                        loading={sendLoading}
                                                        variant="contained"
                                                        type="submit"
                                                    >
                                                        Send Reset Link
                                                    </LoadingButton>
                                                </div>
                                                <Button
                                                    color="primary"
                                                    onClick={() => navigate('/')}
                                                    variant="text"
                                                    sx={{ mt: 2 }}
                                                >
                                                    Back to Sign In
                                                </Button>
                                            </CardContent>
                                        </Card>
                                    </form>
                                )}

                                {step === 2 && (
                                    <form onSubmit={resetForm.handleSubmit(handleResetPassword)}>
                                        <Card
                                            sx={{ backgroundColor: 'background.default' }}
                                            elevation={0}
                                        >
                                            <CardContent>
                                                <Box sx={{ alignItems: 'center', display: 'flex', mb: 3 }}>
                                                    <Typography color="textPrimary" variant="h4">
                                                        Reset Password
                                                    </Typography>
                                                    <Box sx={{ flexGrow: 1 }} />
                                                </Box>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                                    A reset token was sent to <strong>{emailSent}</strong>. Paste it below along with your new password.
                                                </Typography>
                                                <div className="flex flex-col gap-4">
                                                    <div className="w-full">
                                                        <TextInput
                                                            control={resetForm.control}
                                                            name="token"
                                                            label="Reset Token"
                                                            value={resetForm.watch('token')}
                                                        />
                                                    </div>
                                                    <div className="w-full">
                                                        <TextInput
                                                            control={resetForm.control}
                                                            name="password"
                                                            label="New Password"
                                                            value={resetForm.watch('password')}
                                                            type="password"
                                                        />
                                                    </div>
                                                    <div className="w-full">
                                                        <TextInput
                                                            control={resetForm.control}
                                                            name="confirm_password"
                                                            label="Confirm New Password"
                                                            value={resetForm.watch('confirm_password')}
                                                            type="password"
                                                        />
                                                    </div>
                                                    <LoadingButton
                                                        fullWidth
                                                        color="primary"
                                                        loading={resetLoading}
                                                        variant="contained"
                                                        type="submit"
                                                    >
                                                        Reset Password
                                                    </LoadingButton>
                                                </div>
                                                <Button
                                                    color="primary"
                                                    onClick={() => setStep(1)}
                                                    variant="text"
                                                    sx={{ mt: 2 }}
                                                >
                                                    Use a different email
                                                </Button>
                                            </CardContent>
                                        </Card>
                                    </form>
                                )}
                            </div>
                        </div>
                    </Container>
                </Box>
            </Box>
        </>
    );
};

export default ForgotPassword;
